import { useBreakpoint } from '@vivino/js-react-common-ui';
import { useI18N } from '@vivino/js-web-common';
import PropTypes from 'prop-types';
import React from 'react';
import Slider from 'vivino-js/utilities/Slider';
import TasteNoteBadge from 'vivino-ui/icons/TasteNote/72';

import { BaseModal, ModalSize, PADDING_NONE } from '@components/BaseModal/BaseModal';

import CommunityReviewsTrackingContext from 'vivino-ui/components/CommunityReviews/CommunityReviewsTrackingContext';
import CommunityReviewItem, {
  CommunityReviewItemSkeleton,
} from 'vivino-ui/components/CommunityReviews/components/CommunityReviewItem';

import NoteTag from '../NoteTag';
import highlightedReviewNoteGetter from './highlightedReviewNoteGetter';
import styles from './tasteReviews.scss';

const TRANSLATION_PATH = 'components.shared.taste_reviews';
const TRANSLATIONS = {
  showMore: `${TRANSLATION_PATH}.show_more`,
  countMentionsOf: `${TRANSLATION_PATH}.mentions_of_taste_notes`,
};

const TASTE_REVIEW_TRACK = 'tasteLightbox';

const TasteReviews = ({
  show,
  onClose,
  note,
  shownKeywordPairs,
  isLoading,
  onIntersect,
  onNoteTagClick,
  selectedKeywordIds,
  reviews,
  onReviewDeleted,
  scrollToTopUpdater,
  vintageYear,
}) => {
  const { t } = useI18N();
  const { isDesktop } = useBreakpoint();

  return (
    <BaseModal
      show={show}
      onClose={onClose}
      padding={PADDING_NONE}
      onIntersect={onIntersect}
      scrollToTopUpdater={scrollToTopUpdater}
      size={ModalSize.LightBox}
    >
      <div className={styles.tasteReviews}>
        <div className={styles.badgeContainer}>
          <TasteNoteBadge id={note.group} />
        </div>
        <div className={styles.noteText}>
          <span className={styles.capitalizedTastes} key={note.group}>
            {t(`${TRANSLATION_PATH}.${note.group}`)}
          </span>
        </div>
        <div className={styles.mentionsText}>
          {t(TRANSLATIONS.countMentionsOf, {
            count: note.stats.mentions_count,
            taste_group: t(`${TRANSLATION_PATH}.${note.group}`),
          })}
        </div>
        <div className={styles.keywords}>
          {isDesktop && (
            <Slider pageSize={3}>
              {shownKeywordPairs.map((keywordColumn) => (
                <div key={keywordColumn[0].id}>
                  {keywordColumn.map((keyword) => (
                    <NoteTag
                      key={keyword.id}
                      keyword={keyword}
                      selected={selectedKeywordIds.includes(keyword.id.toString())}
                      onClick={onNoteTagClick}
                      noteGroup={note.group}
                    />
                  ))}
                </div>
              ))}
            </Slider>
          )}
          {!isDesktop &&
            note?.primary_keywords?.map((keyword) => (
              <NoteTag
                key={keyword.id}
                keyword={keyword}
                selected={selectedKeywordIds.includes(keyword.id.toString())}
                onClick={onNoteTagClick}
                noteGroup={note.group}
              />
            ))}
        </div>
        <div className={styles.gradientMargin} />
        <div className={styles.reviews}>
          <CommunityReviewsTrackingContext.Provider value={TASTE_REVIEW_TRACK}>
            {reviews?.map((review) => (
              <CommunityReviewItem
                key={review.id}
                review={review}
                getReviewNote={highlightedReviewNoteGetter({ note, selectedKeywordIds, styles })}
                onReviewDeleted={onReviewDeleted}
                vintageYear={vintageYear}
                isUserActionEnabled
                renderReviewContentAsCard
              />
            ))}
          </CommunityReviewsTrackingContext.Provider>
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <CommunityReviewItemSkeleton key={index} />
            ))}
        </div>
      </div>
    </BaseModal>
  );
};

TasteReviews.propTypes = {
  show: PropTypes.bool.isRequired,
  note: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  shownKeywordPairs: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onIntersect: PropTypes.func.isRequired,
  onNoteTagClick: PropTypes.func.isRequired,
  selectedKeywordIds: PropTypes.array.isRequired,
  reviews: PropTypes.array.isRequired,
  onReviewDeleted: PropTypes.func,
  scrollToTopUpdater: PropTypes.number,
  vintageYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default TasteReviews;
